<script setup lang="ts">
import { defineAsyncComponent, ref } from 'vue'
import {getTranslation, setShop, getShopStatus, getSettings} from '@/ts/utilities.ts'
import { MarkerData } from '@/interfaces/MapsViewInterface'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))
const Modal = defineAsyncComponent(() => import('@/components/helpers/Modal.vue'))
const Collapse = defineAsyncComponent(() => import('@/components/ui/Collapse.vue'))
const settings = getSettings()
interface Props {
	dealer: MarkerData,
	showWithStock: false
}

const props = defineProps<Props>()

const changingShop = ref(false)
const changingShopRedirectLink = ref('')
const changingShopRedirectText = ref('')
const openSection = ref(false)

const dealer = props.dealer

const changeShop = async (dealer: MarkerData) => {
	try {
		const dealerLink = dealer.Link
		const data = await setShop(dealer, false)
		if (data.success) {
			if (!data.beforeRedirectConfirmation) {
				location.reload();
			} else {
				changingShop.value = true
				changingShopRedirectLink.value = data.redirectLink
				changingShopRedirectText.value = data.beforeRedirectConfirmationText
			}
		}
	} catch (error) {
		console.error('Error changing shop', error)
	}
}

const redirectShop = (redirectLink: string) => {
	location.href = redirectLink
}


function close() {
	changingShop.value = false
	if (document.documentElement.classList.contains('overflow-hidden') && document.documentElement.classList.contains('modal-open')) {
		document.documentElement.classList.remove('overflow-hidden', 'modal-open')
	}
}

</script>

<template>
	<div :data-dealer-card="dealer.MenuText" class="flex flex-col relative gap-4 justify-center bg-white hover:no-underline py-8 px-6 rounded-md border border-grey hover:shadow">
		<div class="flex">
			<address class="not-italic flex-grow">
				<p class="text-lg">{{ dealer.ShopName }}</p>
				{{ dealer.Address }}<br>
				{{ dealer.PostalAndCity }}
			</address>
			<span class="text-primary font-bold text-sm" v-if="showWithStock && dealer.ShopStockProductOnStock">
			<font-awesome-icon :icon="['fal', 'check']" /> {{getTranslation('Product in stock', 'På lager')}}
			</span>
		</div>

		<RenderAction
			action="button"
			theme="new-dark"
			size="sm"
			class="w-fit"
			@click="changeShop(dealer)"
		>
      {{ getTranslation('HeaderFindShop') }}
		</RenderAction>
		<div class="flex justify-between items-center">
			<div class="text-sm">
				<span v-if="dealer?.CurrentOpeningInfoShow">
					<span class="flex gap-1" v-if="!dealer?.CurrentOpeningInfoHasMore">
						<span class="text-primary font-bold" v-if="dealer?.CurrentOpeningInfoOpen">{{ getTranslation('Shop is Open', 'Åben') }}</span>
						<span class="text-black font-bold" v-else>{{ getTranslation('Shop is Closed', 'Lukket') }}</span>
						<span>·</span>
						<span>{{ dealer?.CurrentOpeningInfoText }}</span>
					</span>
				</span>
			</div>
			<RenderAction
				action="button"
				theme="link"
				size="sm"
				class="underline active:underline hover:underline focus:underline"
				:aria-expanded="openSection"
				@click="openSection = !openSection"
			>
				{{ getTranslation('openinghours') }}
				<font-awesome-icon v-if="openSection" :icon="['fas', 'chevron-up']"/>
				<font-awesome-icon v-else :icon="['fas', 'chevron-down']"/>
			</RenderAction>
		</div>
		<Collapse v-model="openSection">
			<strong class="mb-1 block">
				{{ getTranslation('FindShopOpeningHoursHeader') }}
			</strong>
			<div v-if="dealer?.ShopInformationList?.length > 0">
				<div v-for="row in dealer.ShopInformationList"
					 class="flex gap-x-2">
					<div v-if="row.columnOne"
						 :class="row.columnTwo ? 'basis-1/2' : ''">
						{{ row.columnOne }}
					</div>
					<div v-if="row.columnTwo">{{ row.columnTwo }}</div>
				</div>
			</div>
			<div  v-if="dealer?.ShopInformationText"
				 v-html="dealer?.ShopInformationText">
			</div>
			<strong class="mt-4 mb-1 block">
				{{ getTranslation('Shops.ContactInfo') }}
			</strong>
			<p>
				{{ dealer.Phone }}<br>
				<a :href="`mailto:${dealer.Email}`" class="text-black underline">
					{{ dealer.Email }}
				</a>
			</p>
			<div class="flex mt-4">
				<RenderAction
					action="button"
					theme="faded"
					size="sm"
					@click="changeShop(dealer)"
				>
          {{ getTranslation('FindShopCTA') }}
				</RenderAction>
			</div>
		</Collapse>

		<Modal v-if="changingShop" :show="changingShop" @close="changingShop = false">
			<template v-slot:modal-title>
				{{ getTranslation('Skift forhandler') }}
			</template>
			<template v-slot:content>
				<div class="p-8 w-full flex flex-col justify-center items-center gap-8 min-h-[50vh]">
					<div class="text-center">
						{{ changingShopRedirectText }}
					</div>
					<div class="flex items-center gap-2">
						<RenderAction
							action="button"
							theme="link"
							@click="close()"
						>
							{{ getTranslation('ShopRedirect.Cancel') }}
						</RenderAction>
						<RenderAction
							action="button"
							theme="primary"
							@click="redirectShop(changingShopRedirectLink)"
						>
							{{ getTranslation('ShopRedirect.Select') }}
						</RenderAction>
					</div>
				</div>
			</template>
		</Modal>

	</div>
</template>
